// HTTP client for the `split` Worker (src/worker.ts). All requests carry
// the CF Access cookie via `credentials: 'include'`; in dev the Vite
// proxy injects the owner bearer instead (vite.config.ts). Same JSON API
// mobile uses — writes go through the op endpoints (POST/PATCH/DELETE
// /expenses|/settlements → {record, etag, balance}), never a blob PUT.
//
// Ported from japan-trip web/src/lib/api.ts (request envelope, ApiError,
// assertJsonRoute, setUnauthorizedHandler, getMe cache) with the guest-
// token machinery dropped and endpoints re-pointed at /api/households.

import { setPeople } from './people';
import type {
  Balances,
  Expense,
  HouseholdMember,
  HouseholdMeta,
  PairwiseBalance,
  Settlement,
} from '@core/ledger-core';

class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

// Session-expiry hook. AuthGate handles the 401 it gets at mount; this
// covers a session expiring *during* use (a write that 401s mid-session).
// Until a handler is registered this is a no-op and the 401 surfaces to
// the caller as ApiError.
let onUnauthorized: ((path: string) => void) | null = null;
export function setUnauthorizedHandler(fn: ((path: string) => void) | null): void {
  onUnauthorized = fn;
}
export function notifyIfUnauthorized(path: string, status: number): void {
  if (status === 401 && onUnauthorized) onUnauthorized(path);
}

// SPA-fallback guard. With `not_found_handling: single-page-application`
// + `run_worker_first: true`, an /api/* path the Worker doesn't match
// falls through to Workers Assets, which serves index.html as 200 HTML.
// Parsing that as JSON throws a cryptic error; surface the real cause —
// an unmatched API route — instead. Load-bearing (plan §Reuse map).
export function assertJsonRoute(path: string, res: Response): void {
  const ct = res.headers.get('Content-Type') || '';
  if (path.startsWith('/api/') && ct.includes('text/html')) {
    throw new ApiError(res.status, null, `API route not matched (got HTML): ${path}`);
  }
}

// The one fetch in the web app. The Mac app's window adds its auth
// header to same-origin /api/ fetches (CLAUDE.md, "Mac app"), so every
// call, including a file's bytes (`as: 'blob'`), goes through here.
async function request<T>(
  path: string,
  init: RequestInit = {},
  as: 'json' | 'blob' = 'json',
): Promise<T> {
  const res = await fetch(path, {
    ...init,
    credentials: 'include',
    headers: {
      Accept: as === 'blob' ? '*/*' : 'application/json',
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...init.headers,
    },
  });
  notifyIfUnauthorized(path, res.status);
  assertJsonRoute(path, res);
  if (res.status === 204) return undefined as T;
  if (as === 'blob' && res.ok) return (await res.blob()) as T;
  let body: unknown = null;
  const ct = res.headers.get('Content-Type') || '';
  if (ct.includes('application/json')) {
    body = await res.json().catch(() => null);
  } else {
    body = await res.text().catch(() => null);
  }
  if (!res.ok) {
    // Worker errors are { error, message } — prefer the human message.
    const msg =
      body && typeof body === 'object' && 'message' in body
        ? String((body as { message: unknown }).message)
        : body && typeof body === 'object' && 'error' in body
          ? String((body as { error: unknown }).error)
          : `HTTP ${res.status}`;
    throw new ApiError(res.status, body, msg);
  }
  return body as T;
}

export function apiGet<T>(path: string): Promise<T> {
  return request<T>(path, { method: 'GET' });
}
export function apiPost<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, { method: 'POST', body: JSON.stringify(body) });
}
export function apiPatch<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, { method: 'PATCH', body: JSON.stringify(body) });
}
export function apiDelete<T>(path: string, headers?: Record<string, string>): Promise<T> {
  return request<T>(path, { method: 'DELETE', headers });
}
export function apiPut<T>(path: string, body: unknown, headers?: Record<string, string>): Promise<T> {
  return request<T>(path, { method: 'PUT', body: JSON.stringify(body), headers });
}

// ─── response shapes (mirror src/worker.ts handlers) ─────────────────

export interface MeHousehold {
  householdId: string;
  role: 'owner' | 'viewer';
  name: string;
  // The household's BASE currency — the one balances are expressed in
  // and the one baseAmountCents counts. Individual expenses may be
  // written in any supported currency.
  currency: string;
  accent: string;
  members: HouseholdMember[];
  joinedAt: string | null;
  balance: { netCents: number; line: string; baseCurrency?: string };
}
export interface MeResponse {
  email: string;
  households: MeHousehold[];
  /** How to name each person in your households (the people table, plan D19 P6). */
  people?: Array<{ email: string; name: string }>;
}

// Every mutation answers {record, etag, balance, baseCurrency} (+
// deduped on replay). `balance` is denominated in baseCurrency while
// `record.amountCents` is in the record's OWN currency — rendering one
// with the other's symbol is the bug this field exists to prevent.
export interface MutationEnvelope<T> {
  record: T | null;
  deduped?: boolean;
  // Set by an add whose `source` link already exists in this household:
  // `record` is the EXISTING expense and nothing was written. The import
  // surfaces use it to say "already in the ledger" instead of "added".
  duplicateSource?: boolean;
  etag: string;
  balance: Balances;
  baseCurrency?: string;
}

export interface BalanceResponse extends Balances {
  householdId: string;
  line: string;
  // Units of every *Cents field in this body (and of `line`).
  baseCurrency?: string;
  etag: string;
}

// ─── /api/me (cached warm-start + in-flight dedupe) ──────────────────

// A returning member renders optimistically from the cached copy while
// the fresh probe runs behind it (AuthGate). Never cleared on 401 by
// getMe itself — AuthGate owns invalidation.
export const ME_LS = 'ww.web.me.v1';
let meInFlight: Promise<MeResponse> | null = null;

export function cachedMe(): MeResponse | null {
  try {
    const s = localStorage.getItem(ME_LS);
    if (!s) return null;
    const parsed = JSON.parse(s) as MeResponse;
    if (typeof parsed?.email === 'string' && Array.isArray(parsed?.households)) {
      setPeople(parsed.people);
      return parsed;
    }
  } catch {
    // corrupted / storage unavailable — fall back to the blocking probe
  }
  return null;
}

export function clearCachedMe(): void {
  try {
    localStorage.removeItem(ME_LS);
    localStorage.removeItem('ww.web.today.v1');
  } catch {
    // ignore
  }
}

export function getMe(): Promise<MeResponse> {
  if (!meInFlight) {
    meInFlight = apiGet<MeResponse>('/api/me')
      .then((r) => {
        setPeople(r.people);
        try {
          localStorage.setItem(ME_LS, JSON.stringify(r));
        } catch {
          /* private mode / quota — fine, just no warm-start */
        }
        return r;
      })
      .finally(() => {
        meInFlight = null;
      });
  }
  return meInFlight;
}

// "This browser has signed in before" — set on every successful probe,
// deliberately NOT cleared on 401: it distinguishes a lapsed member from
// a first-time visitor so SignInScreen can phrase itself accordingly.
const HAD_SESSION_KEY = 'ww.web.hadSession';
export function hadSessionHere(): boolean {
  try {
    return localStorage.getItem(HAD_SESSION_KEY) === '1';
  } catch {
    return false;
  }
}
export function markHadSession(): void {
  try {
    localStorage.setItem(HAD_SESSION_KEY, '1');
  } catch {
    // ignore
  }
}

// ─── /api/me/today (the Today screen, src/lib/today.ts) ───────────────

export interface TodayTrip {
  tripId: string;
  name: string;
  dateStart: string;
  dateEnd: string | null;
  nights: number | null;
  /** Days from today to the start; 0 or less once the trip is under way. */
  daysUntil: number;
  /** Whose trip it is (email); absent from a Worker before it. */
  owner?: string | null;
  /** Who travels, by name; null = the trip has no traveler roster. */
  travelers?: string[] | null;
  /** false = someone else's trip you can see; null = can't tell. */
  going?: boolean | null;
}

export type AgendaKind =
  | 'flight'
  | 'rail'
  | 'check-in'
  | 'check-out'
  | 'restaurant'
  | 'experience'
  | 'booking'
  | 'pets'
  | 'note'
  | 'remind'
  | 'opens'
  | 'open-now';

/**
 * One dated thing: a Tripper Keeper trip row, (source 'plan', no trip) a
 * reservation outside trips, or (source 'note') a dated note from email
 * capture (src/lib/agenda-core.ts). Dates and times are local to the place.
 */
export interface AgendaItem {
  id: string;
  tripId: string | null;
  tripName: string | null;
  kind: AgendaKind;
  date: string;
  time: string | null;
  endTime: string | null;
  title: string;
  detail: string | null;
  /** A reservation window's table or room date. */
  forDate: string | null;
  confirmation: string | null;
  url: string | null;
  daysUntil: number;
  /** 'trip' (Tripper Keeper), 'plan' (Plans) or 'note' (a note from email). */
  source?: string;
  /** For a 'trip' row: the TK booking it is (Plans edits it); null for a booking window. */
  bookingId?: string | null;
  /** For a trip to-do (kind 'remind'): the to-do Coming up checks off. */
  todoId?: string | null;
  /** For a 'plan' row: the reservation it comes from. */
  reservationId?: string | null;
  /** For a 'plan' row on a day one of your trips covers: that trip. */
  duringTrip?: string | null;
  /** For a 'note' row: the note it comes from (NoteItem) and whose it is. */
  noteId?: string | null;
  noteOwner?: string | null;
  /** depart | arrive | pickup | return | check-in | check-out, or null. */
  role?: string | null;
  /** A plan's own first and last day: a dog stay's drop-off and return, a repeating plan's span. */
  startsOn?: string | null;
  endsOn?: string | null;
  /** "Every Friday" on a repeating plan's rows. */
  repeats?: string | null;
  /** The dogs a 'pets' row is for; on a routine's day, without those skipping it. */
  dogs?: string[] | null;
  /** Dogs skipping a routine's day because they have another plan then. */
  skipping?: string[] | null;
  /** Where, when the plan names a place: "Civilized Wolf". */
  placeName?: string | null;
  /** Who books a window or acts on a reminder (lowercased email); a trip's windows and to-dos reach only them. null = everyone's. */
  booker?: string | null;
}

// ─── Coming up (src/lib/coming-up-core.ts) ───────────────────────────
//
// Copies of the Worker's types, so the web build doesn't break when the
// Worker adds a kind or a field. The Worker decides the blocks and the
// Dogs lines; Today renders them.

export interface ComingUpBlock {
  type: 'trip' | 'plan' | 'window';
  /** Stable key: `trip:{tripId}` or the row's id. */
  key: string;
  /** The block's date badge: the trip's first day in view, or the row's date. */
  date: string;
  daysUntil: number;
  trip: TodayTrip | null;
  /** The row a plan or window block is. */
  item: AgendaItem | null;
  /** Rows under a collapsed trip block: windows first, then the important ones. */
  shown: AgendaItem[];
  /** Rows only an expanded block shows. */
  hidden: AgendaItem[];
  /** "Liz only" on a trip the viewer isn't on. */
  away: string | null;
  /** "During Aloha Japan 2026" (a clash), "Liz away", or "For Portland Staycation" on a dog stay. */
  note: string | null;
  noteTone: 'warn' | 'quiet' | null;
}

export interface ComingUpLater {
  trip: TodayTrip;
  away: string | null;
}

export interface DogsLine {
  key: string;
  /** stay: with a sitter or boarding; appointment: one day (a grooming); routine: the next day of a weekly plan; care: a trip still needing care arranged. */
  type: 'stay' | 'appointment' | 'routine' | 'care';
  reservationId: string | null;
  title: string;
  date: string;
  daysUntil: number;
  time: string | null;
  endTime: string | null;
  /** A stay's last day (the day they're back). */
  until: string | null;
  /** "Every Friday". */
  repeats: string | null;
  dogs: string[] | null;
  /** On a routine's day: the dogs sitting it out. */
  skipping: string[] | null;
  place: string | null;
  /** The trip a stay is for, an appointment falls in, or that needs care. */
  tripId: string | null;
  tripName: string | null;
}

export interface DogsBlock {
  /** With a sitter or boarding today. */
  away: DogsLine | null;
  /** Soonest first, at most 5. */
  next: DogsLine[];
}

export interface ComingUp {
  soon: ComingUpBlock[];
  later: ComingUpLater[];
  /** The Dogs section; absent from a Worker before it. */
  dogs?: DogsBlock;
}

export interface TodayTrips {
  /** false = no Tripper Keeper link saved for this person (see /trips). */
  linked: boolean;
  current: TodayTrip[];
  upcoming: TodayTrip[];
  /** The next 14 days across those trips; absent in a response cached before it existed. */
  agenda?: AgendaItem[];
  fetchedAt: string | null;
  /** true = TK didn't answer; these are the last-known trips. */
  stale: boolean;
  error: string | null;
}

// ─── status (src/lib/status.ts on the Worker) ────────────────────────

export type StatusLevel = 'ok' | 'stale' | 'attention' | 'down';

/** One thing a media check lists (status-core CheckItem), with its level from the Worker. */
export interface StatusItem {
  title: string;
  state: 'downloading' | 'queued' | 'paused' | 'processing' | 'blocked' | 'added';
  detail?: string;
  percent?: number;
  at?: string;
  forSeconds?: number;
  level?: StatusLevel;
}

export interface StatusCheck {
  id: string;
  group: 'machines' | 'services' | 'storage' | 'mounts' | 'backups' | 'media' | 'network';
  label: string;
  ok: boolean;
  text?: string | null;
  metrics?: {
    usedPct?: number;
    freeBytes?: number;
    sizeBytes?: number;
    lastBackupAt?: string;
    running?: boolean;
    percent?: number;
    maxAgeHours?: number;
    failed?: boolean;
    /** Media downloads: new jobs, their bytes, time left; replacements; paused; nothing moving for. */
    jobs?: number;
    bytes?: number;
    bytesLeft?: number;
    secondsLeft?: number;
    replacements?: number;
    replacementBytesLeft?: number;
    replacementsBlocked?: number;
    paused?: boolean;
    resumeInSeconds?: number;
    idleSeconds?: number;
    waitingToProcess?: number;
  };
  /** Where the thing checked is, when that isn't its source's site ("portland"). */
  site?: string;
  /** Media: downloads, stuck imports, titles added. */
  items?: StatusItem[];
  level: StatusLevel;
  reason: string | null;
}

export interface StatusSource {
  source: string;
  site: string;
  label: string;
  /** "mac": a Mac reporting on itself; when it's quiet it's asleep, not a problem. */
  kind?: 'site' | 'mac';
  asleep?: boolean;
  collectedAt: string | null;
  receivedAt: string | null;
  stale: boolean;
  level: StatusLevel;
  checks: StatusCheck[];
  /** Rows made of several of its checks (status-core Composite: Plex); absent from an older Worker. */
  composites?: Array<{ id: string; label: string; parts: string[]; site?: string }>;
}

export interface StatusView {
  householdId: string;
  evaluatedAt: string;
  level: StatusLevel | 'none';
  problems: StatusProblem[];
  sources: StatusSource[];
  /** The viewer's own headline (plan D21): from `counted` problems only. */
  mine?: { level: StatusLevel; count: number; snoozed: number };
}

export interface StatusProblem {
  source: string;
  site: string;
  checkId: string | null;
  /** The check's group; null for a source gone silent. */
  group?: StatusCheck['group'] | null;
  label: string;
  level: StatusLevel;
  reason: string;
  /** When the problem started (ISO 8601); null when unknown, absent from older Workers. */
  since?: string | null;
  /** Backups: the age limit in force, in hours. */
  limitHours?: number;
  /** D21, for the viewer: its area, their snooze, whether it counts and notifies for them. */
  area?: string;
  snoozed?: { until: string | null; level: StatusLevel; at: string } | null;
  notify?: boolean;
  counted?: boolean;
}

// ─── alerts (plan D21; src/lib/alerts.ts on the Worker) ──────────────

export type AlertMode = 'notify' | 'top' | 'section';
export type AlertPreset = 'everything' | 'mine' | 'quiet' | 'custom';

export interface AlertPrefs {
  preset: AlertPreset;
  areas: Record<string, AlertMode>;
  updatedAt?: string;
}

export interface AlertSnooze {
  householdId: string;
  source: string;
  checkId: string | null;
  level: StatusLevel;
  at: string;
  until: string | null;
  label: string;
  reason: string | null;
}

export interface StatusRules {
  version: number;
  defaults: { backupMaxAgeDays: number; diskAttentionPct: number; diskDownPct: number; setBy?: string; setAt?: string };
  checks: Record<string, { maxAgeDays?: number; owner?: string; setBy: string; setAt: string }>;
}

export interface BackupRow {
  source: string;
  checkId: string;
  label: string;
  owner: string | null;
  ownerGuessed: boolean;
  lastBackupAt: string | null;
  reported: boolean;
  lastSeenAt: string | null;
  limit: { days: number; hours: number; from: 'rule' | 'collector' | 'default' };
  rule: { maxAgeDays: number | null; owner: string | null; setBy: string; setAt: string } | null;
}

export interface AlertSettings {
  householdId: string;
  prefs: AlertPrefs;
  prefsSaved: boolean;
  areas: Array<{ id: string; label: string; mode: AlertMode }>;
  snoozes: AlertSnooze[];
  rules: StatusRules;
  limitDays: number[];
  backups: BackupRow[];
  people: Array<{ email: string; name: string }>;
}

export function getAlertSettings(householdId: string): Promise<AlertSettings> {
  return apiGet(`/api/me/alerts?household=${encodeURIComponent(householdId)}`);
}

export function saveAlertPrefs(prefs: { preset: AlertPreset; areas?: Record<string, AlertMode> }): Promise<{ prefs: AlertPrefs }> {
  return apiPut('/api/me/alerts/prefs', prefs);
}

export function snoozeProblem(
  householdId: string,
  p: { source: string; checkId: string | null },
  until: string | null,
): Promise<{ snooze: AlertSnooze }> {
  return apiPost('/api/me/alerts/snoozes', { householdId, source: p.source, checkId: p.checkId, until });
}

export function unsnoozeProblem(householdId: string, p: { source: string; checkId: string | null }): Promise<void> {
  const check = p.checkId ?? '_source';
  return apiDelete(
    `/api/me/alerts/snoozes/${encodeURIComponent(p.source)}/${encodeURIComponent(check)}?household=${encodeURIComponent(householdId)}`,
  );
}

function rulePath(householdId: string, rest: string): string {
  return `/api/households/${encodeURIComponent(householdId)}/status/rules/${rest}`;
}

export function setBackupRule(
  householdId: string,
  source: string,
  checkId: string,
  change: { maxAgeDays?: number | null; owner?: string | null },
  version: number,
): Promise<{ rules: StatusRules }> {
  return apiPut(rulePath(householdId, `${encodeURIComponent(source)}/${encodeURIComponent(checkId)}`), change, {
    'If-Match': String(version),
  });
}

export function removeBackupRule(householdId: string, source: string, checkId: string, version: number): Promise<{ rules: StatusRules }> {
  return apiDelete(rulePath(householdId, `${encodeURIComponent(source)}/${encodeURIComponent(checkId)}`), {
    'If-Match': String(version),
  });
}

export function setRuleDefaults(
  householdId: string,
  change: { backupMaxAgeDays?: number; diskAttentionPct?: number; diskDownPct?: number },
  version: number,
): Promise<{ rules: StatusRules }> {
  return apiPut(rulePath(householdId, 'defaults'), change, { 'If-Match': String(version) });
}

export function getStatus(householdId: string): Promise<StatusView> {
  return apiGet(`/api/households/${encodeURIComponent(householdId)}/status`);
}

export interface TodayHousehold extends Omit<MeHousehold, 'balance'> {
  balance: { netCents: number; line: string; baseCurrency: string; pairwise: PairwiseBalance[] };
  recent: Expense[];
  status?: StatusView;
}

export interface TodayResponse {
  email: string;
  today: string;
  households: TodayHousehold[];
  trips: TodayTrips;
  /** Trip rows plus reservations outside trips (plan D19); absent from a Worker before it. */
  agenda?: AgendaItem[];
  /** The Coming up blocks and the Dogs section; absent from a Worker before them. */
  comingUp?: ComingUp;
  /** What email capture did in the last 48 hours (the viewer's and shared members'), and whether to say so here. */
  captured?: { tellMe: 'email' | 'home'; recent: CaptureLogEntry[] };
  /** The Portland house's next bin pickup (src/lib/bins.ts); null outside the house or before it's set up. */
  bins?: TodayBins | null;
}

/** /api/me/today's `bins`, worded on the Worker (src/lib/bins-core.ts). */
export interface TodayBins {
  day: string;
  daysUntil: number;
  when: 'tonight' | 'today' | 'later';
  garbage: boolean;
  items: Array<'garbage' | 'recycling' | 'glass' | 'compost'>;
  title: string;
  meta: string;
  nextGarbage: string | null;
  holiday: boolean;
  fetchedAt: string;
  stale: boolean;
}

// Warm start like /api/me: Today paints from the last copy while the
// fresh one loads. Per browser, cleared with the /api/me cache on 401.
export const TODAY_LS = 'ww.web.today.v1';

export function cachedToday(): TodayResponse | null {
  try {
    const s = localStorage.getItem(TODAY_LS);
    if (!s) return null;
    const parsed = JSON.parse(s) as TodayResponse;
    if (Array.isArray(parsed?.households) && parsed?.trips) return parsed;
  } catch {
    // corrupted / storage unavailable
  }
  return null;
}

/** `today` is the browser's local date, so countdowns flip at local midnight. */
export async function getToday(today: string, opts: { refresh?: boolean } = {}): Promise<TodayResponse> {
  const q = new URLSearchParams({ today });
  if (opts.refresh) q.set('refresh', '1');
  const r = await apiGet<TodayResponse>(`/api/me/today?${q}`);
  try {
    localStorage.setItem(TODAY_LS, JSON.stringify(r));
  } catch {
    // private mode / quota
  }
  return r;
}

// ─── household endpoints ─────────────────────────────────────────────

const hh = (id: string) => `/api/households/${encodeURIComponent(id)}`;

export function listExpenses(householdId: string): Promise<{
  householdId: string;
  expenses: Expense[];
  etag: string;
}> {
  return apiGet(`${hh(householdId)}/expenses`);
}

export function listSettlements(householdId: string): Promise<{
  householdId: string;
  settlements: Settlement[];
  etag: string;
}> {
  return apiGet(`${hh(householdId)}/settlements`);
}

export function getBalance(householdId: string): Promise<BalanceResponse> {
  return apiGet(`${hh(householdId)}/balance`);
}

// Op-endpoint writes. Bodies are plain records; the Worker mints
// ids/opIds when omitted.
//
// THE TWO AMOUNTS (CLAUDE.md / README §Money and multi-currency):
//   amountCents      integer MINOR UNITS of `currency` — 15234 for
//                    $152.34, 48000 for ¥48,000. Parse it in the UI
//                    with money.ts parseAmountToMinor(input, currency),
//                    never a hardcoded ×100.
//   currency         supported ISO code; omitted = the household base.
//   baseAmountCents  the same money in USD cents. REQUIRED whenever
//                    currency ≠ the household base currency (the server
//                    never applies a rate on a write); for a
//                    base-currency record it must equal amountCents and
//                    may be omitted so the server fills the tautology.

export interface ExpenseBody {
  date: string;
  vendor: string;
  amountCents: number;
  currency?: string;
  baseAmountCents?: number;
  paidBy: string;
  split: Expense['split'];
  note?: string | null;
  dateEnd?: string | null;
}

export function addExpense(
  householdId: string,
  body: ExpenseBody,
): Promise<MutationEnvelope<Expense>> {
  return apiPost(`${hh(householdId)}/expenses`, body);
}

export function updateExpense(
  householdId: string,
  expenseId: string,
  patch: Partial<ExpenseBody>,
): Promise<MutationEnvelope<Expense>> {
  return apiPatch(`${hh(householdId)}/expenses/${encodeURIComponent(expenseId)}`, patch);
}

export function deleteExpense(
  householdId: string,
  expenseId: string,
): Promise<MutationEnvelope<Expense>> {
  return apiDelete(`${hh(householdId)}/expenses/${encodeURIComponent(expenseId)}`);
}

export interface SettlementBody {
  date: string;
  from: string;
  to: string;
  amountCents: number;
  currency?: string;
  baseAmountCents?: number;
  method?: string | null;
  note?: string | null;
}

export function addSettlement(
  householdId: string,
  body: SettlementBody,
): Promise<MutationEnvelope<Settlement>> {
  return apiPost(`${hh(householdId)}/settlements`, body);
}

export function updateSettlement(
  householdId: string,
  settlementId: string,
  patch: Partial<SettlementBody>,
): Promise<MutationEnvelope<Settlement>> {
  return apiPatch(`${hh(householdId)}/settlements/${encodeURIComponent(settlementId)}`, patch);
}

export function deleteSettlement(
  householdId: string,
  settlementId: string,
): Promise<MutationEnvelope<Settlement>> {
  return apiDelete(`${hh(householdId)}/settlements/${encodeURIComponent(settlementId)}`);
}

// ─── FX (optional pre-fill, NEVER a write dependency) ────────────────

// GET /api/fx?from=JPY&to=USD → { from, to, rate, asOf, source }, where
// `rate` converts one MAJOR unit of `from` into major units of `to`.
//
// READ THE ENDPOINT'S HEADER IN src/worker.ts BEFORE USING THIS. The
// rate exists so the base-amount field can be PRE-FILLED for a human to
// confirm — it is not part of writing an expense and must never gate
// one. Failure is a documented 503 { error: 'fx_unavailable' }; the
// correct response is to leave the field blank for manual entry and let
// the save proceed exactly as it would have.
export interface FxQuote {
  from: string;
  to: string;
  rate: number;
  asOf: string;
  source: string;
}

export function getFxRate(from: string, to: string): Promise<FxQuote> {
  return apiGet<FxQuote>(
    `/api/fx?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
  );
}

// ─── Tripper Keeper: the link + "From your trips" ────────────────────
//
// The Split ⇦ TK direction. The row/trip SHAPES are imported as types
// from the Worker's own tk-map module (@core/tk-map) rather than
// re-declared here — this is the same "one canonical module" rule the
// ledger kernel follows, and a re-declared copy would drift the moment
// the annotation gains a field. Type-only, so nothing from tk-map ends
// up in the browser bundle.
export type {
  ImportedMark,
  SuggestedExpense,
  TripBookingRow,
  TripSummary,
} from '@core/tk-map.ts';

// The redacted view of the caller's TK connection. THE TOKEN IS NEVER
// HERE: it lives in the path of the stored mcpUrl, which the Worker
// refuses to echo back. `host` is all a UI needs to say "connected to
// trips.waagewu.com".
export interface TkLinkView {
  linked: boolean;
  host: string | null;
  linkedAt: string | null;
}

export function getTkLink(): Promise<TkLinkView> {
  return apiGet<TkLinkView>('/api/me/tk-link');
}

/** `mcpUrl` is a per-user Tripper Keeper MCP endpoint (GET
 *  /api/me/mcp-token over there). The Worker validates host + shape. */
export function putTkLink(mcpUrl: string): Promise<TkLinkView> {
  return request<TkLinkView>('/api/me/tk-link', {
    method: 'PUT',
    body: JSON.stringify({ mcpUrl }),
  });
}

export function deleteTkLink(): Promise<TkLinkView> {
  return apiDelete<TkLinkView>('/api/me/tk-link');
}

export interface TripBookingsResponse {
  householdId: string;
  household: {
    householdId: string;
    name: string;
    baseCurrency: string;
    members: Array<{ email: string; role: string }>;
  };
  trips: import('@core/tk-map.ts').TripSummary[];
  tripId: string | null;
  bookings: import('@core/tk-map.ts').TripBookingRow[];
  etag: string;
}

/** No tripId = the recent view; a tripId pulls that one trip whatever
 *  its dates. The trip list rides along either way, for the picker. */
export function listTripBookings(
  householdId: string,
  tripId?: string | null,
): Promise<TripBookingsResponse> {
  const q = tripId ? `?tripId=${encodeURIComponent(tripId)}` : '?recent=1';
  return apiGet(`${hh(householdId)}/trip-bookings${q}`);
}

export interface TripImportBody {
  tripId: string;
  bookingId: string;
  // Human corrections only. The amount/currency/date the server actually
  // uses come from ITS OWN re-fetch of the booking — never from here.
  overrides?: {
    date?: string;
    vendor?: string;
    amountCents?: number;
    currency?: string;
    baseAmountCents?: number;
    paidBy?: string;
    split?: Expense['split'];
    note?: string | null;
  };
}

export function importTripBooking(
  householdId: string,
  body: TripImportBody,
): Promise<MutationEnvelope<Expense>> {
  return apiPost(`${hh(householdId)}/trip-bookings/import`, body);
}

// ─── household meta + members (settings surface) ─────────────────────

export function patchHouseholdMeta(
  householdId: string,
  patch: { name?: string; accent?: string },
): Promise<HouseholdMeta> {
  return apiPatch(hh(householdId), patch);
}

export function addMember(
  householdId: string,
  email: string,
  role: 'owner' | 'viewer',
): Promise<{ householdId: string; members: HouseholdMember[] }> {
  return apiPost(`${hh(householdId)}/members`, { email, role });
}

export function removeMember(
  householdId: string,
  email: string,
): Promise<{ householdId: string; members: HouseholdMember[]; removed: string }> {
  return apiDelete(`${hh(householdId)}/members/${encodeURIComponent(email)}`);
}

// ─── house: published homelab pages, files, due items, log (H3a) ─────
//
// A read-only copy of the allowlisted homelab pages (houses/, pets/,
// vehicles/), published by homelab's GitHub Action. Readable by members
// of the household that owns the house data (a 403 for anyone else).
// The maintenance log is the one write, and it lives only in the hub.

export type HouseSection = 'home' | 'dogs' | 'car';

export interface HousePageSummary {
  /** The homelab path, e.g. "pets/frank.md". */
  path: string;
  section: HouseSection;
  title: string;
  updatedAt: string;
}

export interface HousePagesResponse {
  pages: HousePageSummary[];
  commit: string | null;
  publishedAt: string | null;
}

export interface HousePage extends HousePageSummary {
  markdown: string;
  commit: string | null;
}

export interface HouseFile {
  path: string;
  section: HouseSection;
  name: string;
  sha256: string;
  bytes: number;
  contentType: string;
  /** The manifest's device name ("Dryer (GE)"), used to group manuals. */
  device: string | null;
  document: string | null;
  /** The registry device this manual belongs to (H3b), when it has one. */
  deviceId?: string | null;
}

export type DueStatus = 'overdue' | 'due-soon' | 'ok' | 'unscheduled';

export interface DueItem {
  id: string;
  area: HouseSection;
  subject: string;
  task: string;
  dueDate: string | null;
  /** Intervals shorter than a month; wins over everyMonths when both are set. */
  everyDays?: number | null;
  everyMonths: number | null;
  everyMiles: number | null;
  dueMiles: number | null;
  /** A published page, optionally with "#anchor". */
  page: string | null;
  notes: string | null;
  /** The registry device the task is for (H3b); null for dog and car rows. */
  device?: string | null;
  lastDone: { on: string; by: string; note: string | null } | null;
  nextDue: string | null;
  status: DueStatus;
}

export interface MaintenanceLogEntry {
  id: string;
  taskId: string;
  on: string;
  by: string;
  note: string | null;
  createdAt: string;
}

export function getHousePages(): Promise<HousePagesResponse> {
  return apiGet('/api/house/pages');
}

export function getHousePage(path: string): Promise<HousePage> {
  return apiGet(`/api/house/page?path=${encodeURIComponent(path)}`);
}

export function getHouseFiles(section: HouseSection): Promise<{ files: HouseFile[] }> {
  return apiGet(`/api/house/files?section=${encodeURIComponent(section)}`);
}

/** Where a published file's bytes are. A browser opens it in a new tab
 *  (the CF Access cookie rides along); the Mac window can't navigate to
 *  it, because its auth header is only added to fetches, so it reads the
 *  bytes with getHouseFileBlob instead. */
export function houseFileUrl(path: string): string {
  return `/api/house/file?path=${encodeURIComponent(path)}`;
}

export function getHouseFileBlob(path: string): Promise<Blob> {
  return request<Blob>(houseFileUrl(path), { method: 'GET' }, 'blob');
}

/** `today` is the browser's local date (the Worker accepts it within a
 *  day of UTC); `within` is how many days count as "due soon". */
export function getDue(opts: { within?: number; today?: string } = {}): Promise<{ items: DueItem[] }> {
  const q = new URLSearchParams();
  if (opts.within !== undefined) q.set('within', String(opts.within));
  if (opts.today) q.set('today', opts.today);
  const qs = q.toString();
  return apiGet(`/api/house/due${qs ? `?${qs}` : ''}`);
}

// ─── the Portland device registry (H3b, hub plan D14) ───────────────
//
// Rooms and devices from homelab's portland-rooms.tsv and
// portland-devices.tsv, each device with its manuals and due items. The
// category, integration and status values are house-core's (type-only,
// like tk-map's above).

import type { DeviceCategory, DeviceIntegration, DeviceStatus } from '@core/house-core.ts';
export type { DeviceCategory, DeviceIntegration, DeviceStatus };

export interface HouseRoom {
  id: string;
  name: string;
  floor: string | null;
  haArea: string | null;
}

/** One Home Assistant reading (D14's `readings`). */
export interface DeviceReading {
  entity: string;
  kind: string;
  value: number | string | boolean;
  unit: string | null;
}

/** A device's live state once Home Assistant reports (H5). The Worker
 *  sends `live: null` until then. */
export interface DeviceLive {
  updatedAt: string;
  battery: number | null;
  readings: DeviceReading[];
}

export interface HouseDevice {
  id: string;
  name: string;
  /** A HouseRoom id, or null when the room isn't recorded. */
  room: string | null;
  roomName: string | null;
  category: DeviceCategory;
  brand: string | null;
  model: string | null;
  serial: string | null;
  /** YYYY, YYYY-MM or YYYY-MM-DD. */
  installed: string | null;
  /** YYYY, YYYY-MM or YYYY-MM-DD. */
  warrantyUntil: string | null;
  integration: DeviceIntegration;
  haEntities: string[];
  status: DeviceStatus;
  /** A published page, optionally with "#anchor". */
  maintenancePage: string | null;
  notes: string;
  manuals: HouseFile[];
  /** Its due items, computed as /api/house/due computes them. */
  due: DueItem[];
  live: DeviceLive | null;
}

export interface DevicesResponse {
  today: string;
  within: number;
  rooms: HouseRoom[];
  devices: HouseDevice[];
}

function dueQuery(opts: { within?: number; today?: string }, q = new URLSearchParams()): string {
  if (opts.within !== undefined) q.set('within', String(opts.within));
  if (opts.today) q.set('today', opts.today);
  const qs = q.toString();
  return qs ? `?${qs}` : '';
}

export function getDevices(opts: { within?: number; today?: string } = {}): Promise<DevicesResponse> {
  return apiGet(`/api/house/devices${dueQuery(opts)}`);
}

export function getDevice(id: string, opts: { within?: number; today?: string } = {}): Promise<HouseDevice> {
  return apiGet(`/api/house/device${dueQuery(opts, new URLSearchParams({ id }))}`);
}

/** `on` defaults to today on the Worker and may not be after it; pass
 *  the browser's `today` so a local date ahead of UTC still counts. */
export function logMaintenance(
  body: { taskId: string; on?: string; note?: string },
  today?: string,
): Promise<MaintenanceLogEntry> {
  return apiPost(`/api/house/log${today ? `?today=${encodeURIComponent(today)}` : ''}`, body);
}

/** The author or an owner may delete an entry. */
export function deleteMaintenanceLog(id: string): Promise<void> {
  return apiDelete(`/api/house/log/${encodeURIComponent(id)}`);
}

export { ApiError };

// ─── Email capture (src/lib/intake.ts) ───────────────────────────────
//
// The caller's own capture: Gmail connection status, the intake address
// Gmail forwards to, and what was captured. Item shapes are the Worker's
// own types (type-only imports, nothing reaches the bundle).

export type { PendingReservation, MessageSource } from '@core/capture-core.ts';
export type { CostSuggestion, ReviewItem } from '@core/intake-store.ts';
import type { MessageSource, PendingReservation } from '@core/capture-core.ts';
import type { CostSuggestion, ReviewItem } from '@core/intake-store.ts';

export interface IntakeView {
  address: string | null;
  addressCreatedAt: string | null;
  receipts: 'suggest' | 'off';
  /** How a forward to bookings@ reports back: a reply email, or only in Home. */
  tellMe: 'email' | 'home';
  /** Who sees what your mail turns up. */
  share: 'household' | 'private';
  /** Who may act on it. */
  edit: 'household' | 'owner';
  forwarding: { code: string | null; link: string | null; requestedBy: string | null; at: string } | null;
  google: {
    configured: boolean;
    linked: boolean;
    googleEmail: string | null;
    gmail: boolean;
    calendar: boolean;
    status: string | null;
    linkedAt: string | null;
  };
  gmail: {
    lastRunAt: string | null;
    lastError: string | null;
    backfill: string | null;
    queued: number;
    counts: { looked: number; read: number; captured: number; receipts: number } | null;
  } | null;
  open: { reservations: number; suggestions: number; review: number };
}

/**
 * Mail a person sent in themselves that isn't a reservation or a receipt,
 * kept as a note (src/lib/intake-store.ts NoteItem). Copied here rather
 * than imported so the web builds against a Worker from before notes.
 */
export interface NoteItem {
  id: string;
  owner: string;
  status: 'open' | 'done' | 'dismissed';
  /** One line from the reader, else the subject. */
  summary: string;
  subject: string;
  dateStart: string | null;
  dateEnd: string | null;
  time: string | null;
  /** The reader's name for this kind of thing ("house cleaning"), or null. */
  suggestedType: string | null;
  source: MessageSource;
  createdAt: string;
  updatedAt: string;
}

/** One thing email capture did (src/lib/intake-store.ts CaptureLogEntry); /api/me/today's `captured.recent`. */
export interface CaptureLogEntry {
  at: string;
  /** The item key, mail-{hash}-{n}. */
  id: string;
  /** 'note': kept as a note. 'nothing': a person's own email that came to nothing (`reason` says why). */
  op: 'trip-add' | 'plan-add' | 'updated' | 'cancelled' | 'review' | 'suggestion' | 'note' | 'nothing' | 'feedback';
  label: string;
  dateStart: string | null;
  tripId: string | null;
  tripName: string | null;
  /** The email's subject, on entries for mail a person sent in. */
  subject?: string | null;
  reason?: string | null;
  /** Whose mail it was (email); absent from a Worker before shared logs. */
  owner?: string;
}

/** Items come from the viewer's mail and the household members' who share theirs; canAct = the viewer may act on it. */
export interface CapturedLists {
  reservations: (PendingReservation & { canAct?: boolean })[];
  suggestions: (CostSuggestion & { canAct?: boolean })[];
  review: (ReviewItem & { canAct?: boolean })[];
  /** Absent from a Worker before notes. */
  notes?: (NoteItem & { canAct?: boolean })[];
}

export type CapturedListName = keyof CapturedLists;

export function getIntake(): Promise<IntakeView> {
  return apiGet<IntakeView>('/api/me/intake');
}

export function setReceiptSuggestions(receipts: 'suggest' | 'off'): Promise<IntakeView> {
  return apiPatch<IntakeView>('/api/me/intake', { receipts });
}

export function setIntakeSettings(patch: Partial<Pick<IntakeView, 'receipts' | 'tellMe' | 'share' | 'edit'>>): Promise<IntakeView> {
  return apiPatch<IntakeView>('/api/me/intake', patch);
}

/** A new intake address; the old one stops working at once. */
export function newIntakeAddress(): Promise<IntakeView> {
  return apiPost<IntakeView>('/api/me/intake/address', {});
}

export function removeIntakeAddress(): Promise<IntakeView> {
  return apiDelete<IntakeView>('/api/me/intake/address');
}

export function disconnectGoogle(): Promise<unknown> {
  return apiDelete<unknown>('/api/me/google-link');
}

export function getCaptured(): Promise<CapturedLists> {
  return apiGet<CapturedLists>('/api/me/intake/items');
}

/** `done` is for notes only. */
export function actOnCaptured(
  list: CapturedListName,
  id: string,
  body: { action: 'dismiss' | 'restore' | 'file' | 'add' | 'done'; tripId?: string; householdId?: string; baseAmountCents?: number; owner?: string },
): Promise<{ item: unknown; expenseId?: string; bookingId?: string | null }> {
  return apiPost(`/api/me/intake/items/${list}/${encodeURIComponent(id)}`, body);
}

/** Where Settings sends the browser to connect Gmail (a navigation, not a fetch). */
export const CONNECT_GMAIL_PATH = '/auth/google/start?want=gmail';

// ─── reservations outside trips (src/lib/reservations.ts, plan D19) ──

/** 'pets': the dogs with a sitter, boarding or daycare, from the day they go to the day they're back. */
export type ReservationKind = 'restaurant' | 'ticket' | 'experience' | 'hotel' | 'flight' | 'rail' | 'car' | 'pets' | 'other';
export type ReservationStatus = 'idea' | 'to_book' | 'booked' | 'cancelled';

export interface ReservationOpening {
  /** The day the table or seat is for. */
  date: string;
  /** When booking opens, UTC ISO. */
  at: string;
}

export interface Reservation {
  id: string;
  householdId: string;
  kind: ReservationKind;
  status: ReservationStatus;
  /** Booked and over. */
  done: boolean;
  title: string;
  placeRef: string | null;
  place: { name?: string; address?: string; city?: string; region?: string; country?: string } | null;
  tz: string | null;
  startsOn: string | null;
  startTime: string | null;
  endsOn: string | null;
  endTime: string | null;
  candidateDates: string[] | null;
  window: { url: string | null; note: string | null; openings: ReservationOpening[] } | null;
  opensAt: string | null;
  platform: string | null;
  confirmation: string | null;
  manageUrl: string | null;
  cancelBy: string | null;
  partySize: number | null;
  people: Array<{ email: string } | { name: string }> | null;
  booker: string | null;
  visibility: 'household' | 'private';
  /** Who may change it: either member, or only its creator. Only the creator sets this or `visibility`. */
  edit: 'household' | 'creator';
  /** The ledger expense this plan's cost went to (Log cost), and its household. */
  expenseId?: string | null;
  expenseHouseholdId?: string | null;
  amountMinor: number | null;
  currency: string | null;
  notes: string | null;
  /**
   * Free-form extras (src/lib/reservations-core.ts). The ones clients read:
   * `repeat` {every: 'week'} (every week on startsOn's weekday, until endsOn
   * or for good), `dogs` (['frank', 'pepper']) and, on a weekly dog plan,
   * `skipNotice` {daysBefore, note}.
   */
  detail?: {
    repeat?: { every: 'week' } | null;
    dogs?: string[] | null;
    skipNotice?: { daysBefore: number; note: string } | null;
    [key: string]: unknown;
  };
  source: 'web' | 'mcp' | 'mail' | 'tk';
  createdBy: string;
  updatedBy: string;
  createdAt: string;
  updatedAt: string;
  version: number;
  deletedAt: string | null;
}

/** The fields a form sends; null clears one on a change. */
export type ReservationInput = Partial<
  Pick<
    Reservation,
    | 'kind'
    | 'status'
    | 'title'
    | 'place'
    | 'tz'
    | 'startsOn'
    | 'startTime'
    | 'endsOn'
    | 'endTime'
    | 'candidateDates'
    | 'platform'
    | 'confirmation'
    | 'manageUrl'
    | 'cancelBy'
    | 'partySize'
    | 'booker'
    | 'visibility'
    | 'edit'
    | 'notes'
  > & { window: { url?: string | null; openings: ReservationOpening[] } | null }
>;

const reservationsPath = (householdId: string) => `/api/households/${encodeURIComponent(householdId)}/reservations`;

/** A TK trip booking as Plans edits it (GET/PATCH /api/me/trip-bookings/{tripId}/{bookingId}). */
export interface TripBooking {
  tripId: string;
  id: string;
  kind: string | null;
  label: string | null;
  dateStart: string | null;
  time: string | null;
  partySize: number | null;
  confirmation: string | null;
  cancelBy: string | null;
  platform: string | null;
  /** 'cancelled' or null. */
  status: string | null;
}

export type TripBookingPatch = Partial<Pick<TripBooking, 'time' | 'partySize' | 'confirmation' | 'cancelBy' | 'platform' | 'status'>>;

function tripBookingPath(tripId: string, bookingId: string): string {
  return `/api/me/trip-bookings/${encodeURIComponent(tripId)}/${encodeURIComponent(bookingId)}`;
}

export function getTripBooking(tripId: string, bookingId: string): Promise<{ booking: TripBooking }> {
  return apiGet(tripBookingPath(tripId, bookingId));
}

/** Changes the booking in Tripper Keeper, as you (its rules decide). */
export function updateTripBooking(tripId: string, bookingId: string, patch: TripBookingPatch): Promise<{ changed: string[]; booking: TripBooking | null }> {
  return apiPatch(tripBookingPath(tripId, bookingId), patch);
}

/** A trip to-do opened on Today (GET /api/me/trip-todos/{tripId}/{todoId}). */
export interface TripTodo {
  tripId: string;
  id: string;
  title: string;
  due: string | null;
  done: boolean;
  /** The note the to-do came from; null when none. */
  details: string | null;
  /** Its catalog entry in Tripper Keeper, when it has one. */
  url: string | null;
}

function tripTodoPath(tripId: string, todoId: string): string {
  return `/api/me/trip-todos/${encodeURIComponent(tripId)}/${encodeURIComponent(todoId)}`;
}

export function getTripTodo(tripId: string, todoId: string): Promise<{ todo: TripTodo }> {
  return apiGet(tripTodoPath(tripId, todoId));
}

/** Checks the to-do off (or back on) in Tripper Keeper, as you. */
export function setTripTodoDone(tripId: string, todoId: string, done: boolean): Promise<{ done: boolean }> {
  return apiPatch(tripTodoPath(tripId, todoId), { done });
}

export function listReservations(
  householdId: string,
  q: { from?: string; to?: string; includeTrips?: boolean } = {},
): Promise<{ reservations: Reservation[]; tripRows?: AgendaItem[]; tripsError?: string | null }> {
  const params = new URLSearchParams();
  if (q.from) params.set('from', q.from);
  if (q.to) params.set('to', q.to);
  // Your trip bookings for the next 60 days, read from Tripper Keeper as you.
  if (q.includeTrips) params.set('includeTrips', '1');
  const qs = params.toString();
  return apiGet(`${reservationsPath(householdId)}${qs ? `?${qs}` : ''}`);
}

/** `opId` makes a retried save land once. */
export function createReservation(householdId: string, body: ReservationInput, opId: string): Promise<{ reservation: Reservation }> {
  return apiPost(reservationsPath(householdId), { ...body, opId });
}

/** `ifVersion` is the version you loaded; a newer one on the server is refused (412). */
export function updateReservation(householdId: string, id: string, body: ReservationInput, ifVersion: number): Promise<{ reservation: Reservation }> {
  return apiPatch(`${reservationsPath(householdId)}/${id}`, { ...body, ifVersion });
}

/** A plan's cost, logged once in the ledger and linked to the plan (POST …/reservations/{id}/cost). */
export interface PlanCostBody {
  amountCents?: number;
  currency?: string;
  baseAmountCents?: number;
  paidBy?: string;
  split?: Expense['split'];
  date?: string;
}

export function logReservationCost(householdId: string, id: string, body: PlanCostBody): Promise<MutationEnvelope<Expense>> {
  return apiPost(`${reservationsPath(householdId)}/${id}/cost`, body);
}

export function removeReservation(householdId: string, id: string): Promise<{ reservation: Reservation }> {
  return apiDelete(`${reservationsPath(householdId)}/${id}`);
}

export function restoreReservation(householdId: string, id: string): Promise<{ reservation: Reservation }> {
  return apiPost(`${reservationsPath(householdId)}/${id}/restore`, {});
}

// ─── app feedback (src/lib/feedback.ts, docs/feedback/README.md) ─────
//
// One store for feedback about every app; a Claude routine turns each
// new item into a pull request for Jake. Shapes copied from the Worker's
// feedback-core.ts rather than imported, so the web builds against a
// Worker from before feedback.

export type FeedbackApp = 'home' | 'split' | 'trips' | 'mac';
export type FeedbackSurface = 'web' | 'ios' | 'mac' | 'email' | 'mcp';
export type FeedbackStatus = 'new' | 'triaged' | 'in_progress' | 'done' | 'declined';

export interface FeedbackItem {
  /** 'fb_' + ULID. */
  id: string;
  /** Who sent it (email). */
  author: string;
  /** null = the sender didn't say. */
  app: FeedbackApp | null;
  surface: FeedbackSurface;
  /** The page or screen it was sent from, or the email subject. */
  where: string | null;
  version: string | null;
  body: string;
  status: FeedbackStatus;
  /** What the author is told (Jake or the routine). */
  response: string | null;
  /** The pull request or commit the work is in (https only). */
  link: string | null;
  createdAt: string;
  updatedAt: string;
  updatedBy: string;
}

export interface FeedbackInput {
  body: string;
  app?: FeedbackApp | null;
  surface?: FeedbackSurface;
  where?: string | null;
  version?: string | null;
  /** Makes a retried send land once: the same opId answers the first item with `duplicate: true`. */
  opId?: string;
}

export function sendFeedback(input: FeedbackInput): Promise<{ feedback: FeedbackItem; duplicate: boolean }> {
  return apiPost('/api/feedback', input);
}

/** Newest first, at most 200: your own, or everyone's when `canManage` (the owner). */
export function listFeedback(
  q: { status?: FeedbackStatus[] } = {},
): Promise<{ feedback: FeedbackItem[]; canManage: boolean }> {
  const qs = q.status?.length ? `?${new URLSearchParams({ status: q.status.join(',') })}` : '';
  return apiGet(`/api/feedback${qs}`);
}

/** Owner only (403 owner_only). null or '' clears `response` or `link`. */
export function updateFeedback(
  id: string,
  patch: { status?: FeedbackStatus; response?: string | null; link?: string | null },
): Promise<{ feedback: FeedbackItem }> {
  return apiPatch(`/api/feedback/${encodeURIComponent(id)}`, patch);
}

// ─── Reminders lists (plan D22, src/lib/lists.ts) ──────────────────────
// Apple Reminders is the source of truth; a Mac mirrors the chosen lists
// and applies the changes queued here within a minute or so.

export type ListOpKind = 'add' | 'complete' | 'uncomplete' | 'edit' | 'delete';

export interface ListItem {
  /** The reminder's id, or the add's op id (lop_…) while it's on its way. */
  id: string;
  title: string;
  notes: string | null;
  due: string | null;
  priority: 'high' | 'medium' | 'low' | null;
  url: string | null;
  completedAt: string | null;
  createdAt: string | null;
  pending: ListOpKind | null;
  pendingBy: string | null;
}

export interface ReminderList {
  id: string;
  title: string;
  color: string | null;
  addedBy: string;
  syncedAt: string | null;
  syncedBy: string | null;
  stale: boolean;
  openCount: number;
  pendingCount: number;
  items: ListItem[];
  failures: Array<{ opId: string; kind: ListOpKind; title: string | null; by: string; error: string; at: string }>;
}

export interface ListsResponse {
  householdId: string;
  lists: ReminderList[];
  available: Array<{ title: string; color: string | null; devices: string[] }>;
  devices: Array<{ device: string; email: string; seenAt: string }>;
}

export interface ListOpBody {
  kind: ListOpKind;
  itemId?: string;
  title?: string;
  notes?: string | null;
  due?: string | null;
}

export function getLists(householdId: string): Promise<ListsResponse> {
  return apiGet(`/api/households/${householdId}/lists`);
}

export function showList(householdId: string, title: string, color: string | null): Promise<{ list: ReminderList }> {
  return apiPost(`/api/households/${householdId}/lists`, { title, color });
}

export function hideList(householdId: string, listId: string): Promise<void> {
  return apiDelete(`/api/households/${householdId}/lists/${listId}`);
}

export function sendListOps(
  householdId: string,
  listId: string,
  ops: ListOpBody[],
  opId: string,
): Promise<{ list: ReminderList; opIds: string[] }> {
  return apiPost(`/api/households/${householdId}/lists/${listId}/ops`, { ops, opId, source: 'web' });
}

// ─── bank and card accounts (src/lib/bank.ts, plan D23) ──────────────

export interface BankAccount {
  id: string;
  /** The household's nickname when set, else the bank's name. */
  name: string;
  bankName: string;
  officialName: string | null;
  mask: string | null;
  type: string | null;
  subtype: string | null;
  institution: string | null;
  currency: string | null;
  currentCents: number | null;
  availableCents: number | null;
  limitCents: number | null;
  currentText: string | null;
  availableText: string | null;
  balanceAt: string | null;
  /** Joint: household members see it and its transactions. */
  joint: boolean;
}

/** A joint account another member connected. */
export interface SharedBankAccount extends BankAccount {
  owner: string;
  syncedAt: string | null;
}

export interface BankItem {
  id: string;
  institution: string | null;
  institutionId: string | null;
  status: 'ok' | 'login_required' | 'pending_disconnect' | 'revoked' | 'error';
  statusText: string | null;
  newAccounts: boolean;
  consentExpiresAt: string | null;
  /** Plaid's history pull: NOT_READY, INITIAL_UPDATE_COMPLETE (30 days), HISTORICAL_UPDATE_COMPLETE (two years). */
  history: string | null;
  syncedAt: string | null;
  syncError: string | null;
  createdAt: string;
  accounts: BankAccount[];
}

export interface BankResponse {
  env: 'sandbox' | 'production';
  configured: boolean;
  items: BankItem[];
  shared: SharedBankAccount[];
  /** Plaid Trial: connections ever made (removed ones count); limit null in Sandbox. */
  connections: { used: number; limit: number | null };
}

export function getBank(): Promise<BankResponse> {
  return apiGet<BankResponse>('/api/me/bank');
}

export function bankLinkToken(itemId?: string): Promise<{ linkToken: string; expiration: string; mode: 'new' | 'update' }> {
  return apiPost('/api/me/bank/link-token', itemId ? { itemId } : {});
}

export function addBankItem(publicToken: string, institutionName: string | null): Promise<{ item: BankItem }> {
  return apiPost('/api/me/bank/items', { publicToken, institutionName });
}

export function syncBankItem(id: string, refresh = false): Promise<{ item: BankItem; refreshed: boolean }> {
  return apiPost(`/api/me/bank/items/${id}/sync`, { refresh });
}

export function removeBankItem(id: string): Promise<{ removed: string }> {
  return apiDelete(`/api/me/bank/items/${id}`);
}

export function setBankAccount(id: string, patch: { share?: 'owner' | 'household'; nickname?: string | null }): Promise<{ account: BankAccount }> {
  return apiPatch(`/api/me/bank/accounts/${encodeURIComponent(id)}`, patch);
}
